import { BaseElement } from 'base';

class QtyCounter extends BaseElement {
  #action = null;
  #disabled = false;

  onConnect() {
    this.listen(this, 'click', this.onClick);
  }

  run(action) {
    this.#action = action;
    return this;
  }

  setDisabled(isDisabled) {
    this.#disabled = Boolean(isDisabled);
    this.#render(this.value);
    return this;
  }

  setLimits({ step = 1, min = 1, max = Infinity, base } = {}) {
    const nextMin = this.#toInt(min, 1);
    const nextMax = Math.max(this.#toInt(max, Infinity), nextMin);
    this.dataset.step = String(Math.max(1, this.#toInt(step, 1)));
    this.dataset.min = String(nextMin);
    if (base === undefined) {
      delete this.dataset.base;
    } else {
      this.dataset.base = String(this.#toInt(base, nextMin));
    }
    if (Number.isFinite(nextMax)) {
      this.dataset.max = String(nextMax);
    } else {
      delete this.dataset.max;
    }
    this.#render(nextMin);
    return this;
  }

  get value() {
    return this.#toInt(this.dataset.value, this.min);
  }

  get step() {
    return Math.max(1, this.#toInt(this.dataset.step, 1));
  }

  get min() {
    return this.#toInt(this.dataset.min, 1);
  }

  get base() {
    return this.#toInt(this.dataset.base, this.min);
  }

  get max() {
    return this.dataset.max === undefined
      ? Infinity
      : this.#toInt(this.dataset.max, Infinity);
  }

  get removesAtMin() {
    return this.dataset.minAction === 'remove';
  }

  onClick(event) {
    const button = event.target.closest('[data-counter-direction]');
    if (!button || this.#disabled) return;
    if (button.getAttribute('aria-disabled') === 'true') return;
    if (this.getAttribute('aria-busy') === 'true') return;

    const direction = this.#toInt(button.dataset.counterDirection, 0);
    if (direction === 0) return;

    const previous = this.value;

    if (direction < 0 && previous <= this.#floor() && this.removesAtMin) {
      this.#runAction({ value: 0, previous, reason: 'remove' });
      return;
    }

    const next = this.#next(previous, direction);
    if (next === previous) return;

    this.#render(next);
    this.#runAction({
      value: next,
      previous,
      reason: direction > 0 ? 'increase' : 'decrease'
    });
  }

  async #runAction(change) {
    if (!this.#action) return;

    const pending = this.#action(change);
    if (!pending || typeof pending.then !== 'function') return;

    this.setAttribute('aria-busy', 'true');
    try {
      await pending;
    } catch (error) {
      console.error('[counter] action failed:', error);
      if (this.isConnected && change.reason !== 'remove')
        this.#render(change.previous);
    } finally {
      if (this.isConnected) this.removeAttribute('aria-busy');
    }
  }

  #render(value) {
    this.dataset.value = String(value);

    this.qa('[data-counter-value]').forEach(output => {
      output.textContent = String(value);
    });

    this.#setBlocked(
      this.q('[data-counter-direction="-1"]'),
      this.#disabled || (!this.removesAtMin && value <= this.#floor())
    );
    this.#setBlocked(
      this.q('[data-counter-direction="1"]'),
      this.#disabled || value >= this.#ceiling()
    );
  }

  get #hasBase() {
    return this.dataset.base !== undefined;
  }

  #floor() {
    return this.#hasBase ? Math.max(this.base, this.min) : this.min;
  }

  #next(previous, direction) {
    const ceiling = this.#ceiling();
    const { base, step } = this;

    if (!this.#hasBase) {
      return Math.min(Math.max(previous + direction * step, this.min), ceiling);
    }

    const offset = (((previous - base) % step) + step) % step;

    if (direction < 0) {
      if (previous <= this.#floor()) return previous;
      return Math.max(previous - (offset || step), this.#floor());
    }

    const next = previous < base ? base : previous + step - offset;
    return next > ceiling ? previous : next;
  }

  #ceiling() {
    const { min, max, step } = this;
    if (max === Infinity) return Infinity;

    if (this.#hasBase) {
      const { base, value } = this;
      if (max < base) return value;
      return Math.max(base + Math.floor((max - base) / step) * step, value);
    }

    if (max <= min) return min;
    return min + Math.floor((max - min) / step) * step;
  }

  #setBlocked(button, blocked) {
    if (!button) return;

    if (blocked) {
      button.setAttribute('aria-disabled', 'true');
    } else {
      button.removeAttribute('aria-disabled');
    }
  }

  #toInt(raw, fallback) {
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : fallback;
  }
}

if (!customElements.get('qty-counter')) {
  customElements.define('qty-counter', QtyCounter);
}
